/**
 * Authenticated shell: header with user/roles + sign-out, role-filtered
 * nav, and a routed content outlet.
 *
 * Nav items advertise a `requires: AppRole` — the shell filters them via
 * `useRoles().has()` so customers don't see staff links (and vice versa).
 * This is the *first* of three enforcement layers: nav filter here, route
 * guard next (RequireRole), backend auth middleware as the authoritative
 * final word. Never rely on nav-filtering alone for security.
 */
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { useCurrentUser } from "@/hooks/auth/useCurrentUser";
import { useRoles, type AppRole } from "@/hooks/auth/useRoles";
import { useSignOut } from "@/hooks/auth/useSignOut";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  to: string;
  requires: AppRole; // floor role; staff hierarchy expands via has()
}

const NAV: NavItem[] = [
  { label: "Home", to: "/home", requires: "customer" },
  { label: "Agent Desktop", to: "/agent", requires: "agent" },
  { label: "Dashboard", to: "/dashboard", requires: "manager" },
  { label: "Outreach", to: "/outreach", requires: "manager" },
  { label: "Triggers", to: "/triggers", requires: "manager" },
  { label: "Admin", to: "/admin", requires: "admin" },
];

export function AppShell() {
  const { user } = useCurrentUser();
  const { has, roles } = useRoles();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const visible = NAV.filter((item) => has(item.requires));

  async function handleSignOut() {
    await signOut.mutateAsync();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-3">
          <Link to="/home" className="text-lg font-semibold">
            CEX AI
          </Link>
          <nav className="flex flex-1 items-center gap-1">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    isActive
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">{user?.email}</span>
            {roles.length > 0 && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">
                {roles.join(", ")}
              </span>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-md border px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
              disabled={signOut.isPending}
            >
              {signOut.isPending ? "Signing out…" : "Sign out"}
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
